import { captureException } from "./analytics";
import { logger } from "./logger";

// AIDEV-NOTE: Distinguishes genuine browser network failures from HTTP-error
// wrappers that happen to contain the word "fetch" (e.g. an app-level
// `new Error("Failed to fetch invites: 500")`). We match:
//   - our explicit offline marker from useApiClient ("navigator.onLine=false")
//   - the retry-exhaustion wrapper ("API request failed after N attempts: ...")
//   - standalone browser strings ("Failed to fetch" / "Load failed"
//     / "NetworkError ..." / "Network request failed")
//   - navigator.onLine === false as a final fallback
export function isNetworkOfflineError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    const lower = message.toLowerCase();

    if (lower.includes("navigator.online=false")) return true;
    if (/^api request failed after \d+ attempts:/i.test(message)) return true;
    if (
        lower === "failed to fetch" ||
        lower === "load failed" ||
        lower.startsWith("networkerror") ||
        lower.includes("network request failed")
    ) {
        return true;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) return true;
    return false;
}

// AIDEV-NOTE: Use inside a TanStack Query `queryFn` catch block. Offline
// failures log at info and skip Sentry so a flaky client network doesn't
// flood alerts; everything else routes through captureException (logger.error
// + Sentry). Rethrow after calling so React Query still marks the query errored.
export function reportQueryError(error: unknown, context: string): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    if (isNetworkOfflineError(errorObj)) {
        logger
            .withError(errorObj)
            .info(`${context}: browser offline, skipping Sentry capture`);
        return;
    }

    captureException(`Error in ${context}`, errorObj);
}
